import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fundingsHistory } from '../Request/Histories';

/**
 * Funding (history) page — restyled to match the Index dashboard's futuristic
 * layout, theme tokens and skeleton/card aesthetic.
 *
 * Data source (unchanged): fundingsHistory() reads
 * 'binaryfair-account-histories' from localStorage and returns the fundings
 * array. Each item: { id, amount, status, created_at, wallet: { wallet_name } }
 *
 * Theme: same approach as Index.js — defaults to dark unless the user has
 * explicitly chosen light, syncs via the 'themeChanged' event dispatched by
 * SideBar.js / SidebarLarge.js.
 */
const Funding = () => {
  // ---------- theme ----------
  const readTheme = () => {
    const stored = localStorage.getItem('Elliptic Managements-Theme');
    return stored === 'light-mode' ? 'light' : 'dark';
  };
  const [theme, setTheme] = useState(readTheme);
  const isDark = theme === 'dark';

  // ---------- data ----------
  const [fundings, setFundings] = useState(() => fundingsHistory() || []);
  const [activeTab, setActiveTab] = useState('pending'); // pending | approved | declined

  // ---------- effects ----------
  useEffect(() => {
    document.title = 'FUNDING HISTORY';
    if (window && window.init_template) {
      window.init_template();
    }

    const onThemeChange = () => setTheme(readTheme());
    window.addEventListener('themeChanged', onThemeChange);

    const onStorage = (e) => {
      if (e.key === 'Elliptic Managements-Theme') setTheme(readTheme());
      if (e.key === 'binaryfair-account-histories') {
        setFundings(fundingsHistory() || []);
      }
    };
    window.addEventListener('storage', onStorage);

    const onUpdated = () => setFundings(fundingsHistory() || []);
    window.addEventListener('updatedStorage', onUpdated);

    return () => {
      window.removeEventListener('themeChanged', onThemeChange);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('updatedStorage', onUpdated);
    };
  }, []);

  // ---------- helpers ----------
  const fmtAmount = (n) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(parseFloat(n || 0));

  const fmtDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // ---------- rollups ----------
  const sumBy = (predicate) =>
    fundings
      .filter(predicate)
      .reduce((sum, f) => sum + parseFloat(f.amount || 0), 0);

  const pendingTotal = sumBy(
    (f) => f.status === 'pending' || f.status === 'waiting approval'
  );
  const approvedTotal = sumBy((f) => f.status === 'approved');
  const declinedTotal = sumBy((f) => f.status === 'declined');
  const totalFunded = approvedTotal; // approved = "funded"

  const filteredFundings = (() => {
    if (activeTab === 'pending') {
      return fundings.filter(
        (f) => f.status === 'pending' || f.status === 'waiting approval'
      );
    }
    if (activeTab === 'approved') {
      return fundings.filter((f) => f.status === 'approved');
    }
    if (activeTab === 'declined') {
      return fundings.filter((f) => f.status === 'declined');
    }
    return [];
  })().sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const tabCount = (key) => {
    if (key === 'pending') {
      return fundings.filter(
        (f) => f.status === 'pending' || f.status === 'waiting approval'
      ).length;
    }
    if (key === 'approved') {
      return fundings.filter((f) => f.status === 'approved').length;
    }
    if (key === 'declined') {
      return fundings.filter((f) => f.status === 'declined').length;
    }
    return 0;
  };

  return (
    <div className={`dash-root ${isDark ? 'theme-dark' : 'theme-light'}`}>
      <style>{`
        /* ---------- theme tokens (mirrors Index.js) ---------- */
        .dash-root.theme-dark {
          --primary-bg: #0f1816;
          --card-bg: #010907;
          --card-border: rgba(255, 255, 255, 0.05);
          --card-border-hover: rgba(0, 242, 254, 0.3);
          --accent-color: #00f5ff;
          --accent-gradient: linear-gradient(135deg, #00f2fe 0%, #00f5ff 100%);
          --accent-glow: rgba(0, 242, 254, 0.2);
          --accent-soft: rgba(0, 242, 254, 0.1);
          --text-primary: #ffffff;
          --text-secondary: #b0b0b0;
          --glass-effect: rgba(255, 255, 255, 0.05);
          --divider: rgba(255, 255, 255, 0.1);
          --th-bg: rgba(0, 242, 254, 0.1);
          --success-color: #0C860C;
          --danger-color: #d84558;
          --warning-color: #fb8e0d;
          --empty-btn-text: #000;
          --skeleton-base: rgba(255,255,255,0.06);
          --skeleton-shine: rgba(255,255,255,0.14);
        }
        .dash-root.theme-light {
          --primary-bg: #EFEEF3;
          --card-bg: #ffffff;
          --card-border: rgba(15, 23, 42, 0.08);
          --card-border-hover: rgba(0, 153, 178, 0.35);
          --accent-color: #0095b6;
          --accent-gradient: linear-gradient(135deg, #00b8d4 0%, #0095b6 100%);
          --accent-glow: rgba(0, 149, 182, 0.18);
          --accent-soft: rgba(0, 149, 182, 0.08);
          --text-primary: #010907;
          --text-secondary: #475569;
          --glass-effect: rgba(15, 23, 42, 0.04);
          --divider: rgba(15, 23, 42, 0.08);
          --th-bg: rgba(0, 149, 182, 0.08);
          --success-color: #0C860C;
          --danger-color: #d84558;
          --warning-color: #fb8e0d;
          --empty-btn-text: #ffffff;
          --skeleton-base: rgba(15,23,42,0.06);
          --skeleton-shine: rgba(15,23,42,0.12);
        }

        .dash-root {
          color: var(--text-primary);
          font-family: 'Poppins', 'Inter', sans-serif;
          padding: 1.5rem;
          min-height: 100vh;
          background: var(--primary-bg);
          transition: background-color 0.3s ease, color 0.3s ease;
        }
        .dash-root * { box-sizing: border-box; }

        /* ---------- header ---------- */
        .dash-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 0;
          border-bottom: 1px solid var(--divider);
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }
        .dash-greeting h5 {
          font-size: 1.5rem;
          font-weight: 500;
          margin: 0;
          background: var(--accent-gradient);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .dash-greeting p {
          margin: 0.25rem 0 0;
          color: var(--text-secondary);
          font-size: 0.9rem;
        }
        .dash-actions .dash-btn {
          border-radius: 8px;
          font-weight: 500;
          padding: 0.75rem 1.5rem;
          margin-left: 0.75rem;
          transition: all 0.3s ease;
          border: none;
          color: #fff;
          text-decoration: none;
          display: inline-block;
          cursor: pointer;
          box-shadow: 0 4px 15px var(--accent-glow);
        }
        .dash-actions .dash-btn-success { background: var(--success-color); }
        .dash-actions .dash-btn-danger  { background: var(--danger-color); }
        .dash-actions .dash-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px var(--accent-glow);
        }

        /* ---------- stats grid ---------- */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }
        .stat-card {
          background: var(--card-bg);
          border-radius: 16px;
          padding: 1.5rem;
          backdrop-filter: blur(10px);
          border: 1px solid var(--card-border);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }
        .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px var(--accent-glow);
          border-color: var(--card-border-hover);
        }
        .stat-card::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at 70% 20%, var(--accent-soft) 0%, transparent 70%);
          pointer-events: none;
        }
        .stat-icon {
          width: 50px; height: 50px;
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 1rem;
          background: var(--accent-soft);
          color: var(--accent-color);
          font-size: 1.5rem;
        }
        .stat-icon.success { color: var(--success-color); background: rgba(12,134,12,0.12); }
        .stat-icon.warning { color: var(--warning-color); background: rgba(251,142,13,0.12); }
        .stat-icon.danger  { color: var(--danger-color);  background: rgba(216,69,88,0.12); }
        .stat-value {
          font-size: 1.4rem; font-weight: 600; margin: 0.5rem 0;
          background: var(--accent-gradient);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .stat-label { color: var(--text-secondary); font-size: 0.9rem; margin: 0; }

        /* ---------- quick actions ---------- */
        .quick-actions {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-bottom: 2rem;
        }
        .quick-action {
          background: var(--card-bg);
          border: 1px solid var(--card-border);
          border-radius: 16px;
          padding: 1.5rem;
          text-decoration: none;
          color: var(--text-primary);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.5rem;
          text-align: center;
          transition: all 0.3s ease;
          backdrop-filter: blur(10px);
          position: relative;
          overflow: hidden;
        }
        .quick-action:hover {
          transform: translateY(-3px);
          border-color: var(--card-border-hover);
          box-shadow: 0 10px 30px var(--accent-glow);
        }
        .quick-action::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at 50% 0%, var(--accent-soft) 0%, transparent 70%);
          pointer-events: none;
        }
        .quick-action .qa-icon {
          width: 56px; height: 56px;
          border-radius: 16px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.6rem;
          color: #fff;
        }
        .quick-action.fund .qa-icon { background: linear-gradient(135deg, #0a6e0a, #0C860C); }
        .quick-action.withdraw .qa-icon { background: linear-gradient(135deg, #d84558, #ED5565); }
        .quick-action h6 {
          font-size: 1.1rem;
          margin: 0.5rem 0 0;
          font-weight: 600;
          color: var(--text-primary);
        }
        .quick-action p {
          color: var(--text-secondary);
          font-size: 0.85rem;
          margin: 0;
        }

        /* ---------- section ---------- */
        .section-card {
          background: var(--card-bg);
          border-radius: 16px;
          padding: 1.5rem;
          margin-bottom: 2rem;
          backdrop-filter: blur(10px);
          border: 1px solid var(--card-border);
        }
        .section-title {
          font-size: 1.15rem;
          font-weight: 500;
          margin: 0 0 1.25rem;
          color: var(--accent-color);
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        /* ---------- tabs ---------- */
        .tabs-row {
          display: flex;
          gap: 0.5rem;
          background: var(--glass-effect);
          padding: 0.35rem;
          border-radius: 12px;
          margin-bottom: 1.25rem;
          overflow-x: auto;
        }
        .tab-btn {
          flex: 1;
          min-width: 100px;
          background: transparent;
          border: none;
          color: var(--text-secondary);
          padding: 0.6rem 0.9rem;
          border-radius: 8px;
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 500;
          transition: all 0.2s ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.4rem;
          white-space: nowrap;
        }
        .tab-btn:hover {
          color: var(--text-primary);
          background: var(--glass-effect);
        }
        .tab-btn.active {
          background: var(--accent-gradient);
          color: var(--empty-btn-text);
          box-shadow: 0 4px 15px var(--accent-glow);
        }
        .tab-count {
          background: rgba(0,0,0,0.2);
          padding: 0.05rem 0.45rem;
          border-radius: 999px;
          font-size: 0.72rem;
          font-weight: 600;
        }
        .tab-btn:not(.active) .tab-count {
          background: var(--glass-effect);
          color: var(--text-secondary);
        }

        /* ---------- rows ---------- */
        .fund-rows {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .fund-row {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem 1.1rem;
          background: var(--glass-effect);
          border: 1px solid var(--card-border);
          border-radius: 12px;
          transition: all 0.2s ease;
        }
        .fund-row:hover {
          transform: translateX(4px);
          border-color: var(--card-border-hover);
          box-shadow: 0 5px 18px var(--accent-glow);
        }
        .fund-row-icon {
          width: 44px; height: 44px;
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.1rem;
          color: #fff;
          flex-shrink: 0;
        }
        .fund-row-icon.approved { background: linear-gradient(135deg, #0a6e0a, #0C860C); }
        .fund-row-icon.pending  { background: linear-gradient(135deg, #c9760a, #fb8e0d); }
        .fund-row-icon.declined { background: linear-gradient(135deg, #c23048, #d84558); }

        .fund-row-main {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          line-height: 1.25;
        }
        .fund-row-amount {
          font-size: 1rem;
          font-weight: 700;
          color: var(--text-primary);
          font-variant-numeric: tabular-nums;
        }
        .fund-row-date {
          font-size: 0.8rem;
          color: var(--text-secondary);
          margin-top: 2px;
        }

        .fund-row-meta {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          line-height: 1.25;
          gap: 0.25rem;
          flex-shrink: 0;
        }
        .fund-row-wallet {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--text-primary);
          max-width: 140px;
          text-align: right;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .fund-row-status {
          font-size: 0.72rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          padding: 0.18rem 0.55rem;
          border-radius: 6px;
        }
        .fund-row-status.approved { background: rgba(12,134,12,0.14); color: var(--success-color); }
        .fund-row-status.pending  { background: rgba(251,142,13,0.14); color: var(--warning-color); }
        .fund-row-status.declined { background: rgba(216,69,88,0.14); color: var(--danger-color); }

        /* ---------- empty state ---------- */
        .empty-state {
          text-align: center;
          padding: 2.5rem 1rem;
          color: var(--text-secondary);
        }
        .empty-state-icon {
          width: 64px; height: 64px;
          border-radius: 16px;
          background: var(--glass-effect);
          color: var(--accent-color);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 1.6rem;
          margin-bottom: 0.75rem;
        }
        .empty-state h6 {
          margin: 0.5rem 0 0.25rem;
          color: var(--text-primary);
          font-weight: 600;
        }
        .empty-state p { margin: 0 0 1rem; font-size: 0.9rem; }
        .empty-state .dash-btn {
          background: var(--accent-gradient);
          color: var(--empty-btn-text);
          font-weight: 600;
          padding: 0.6rem 1.25rem;
          border-radius: 8px;
          text-decoration: none;
          display: inline-block;
        }

        /* ---------- responsive ---------- */
        @media (max-width: 768px) {
          .stats-grid { grid-template-columns: 1fr 1fr; }
          .dash-header { flex-direction: column; align-items: stretch; }
          .dash-greeting { width: 100%; }
          .dash-actions {
            display: flex;
            flex-direction: column;
            gap: 0.75rem;
            width: 100%;
          }
          .dash-actions .dash-btn {
            width: 100%;
            margin-left: 0;
            text-align: center;
            padding: 1rem 1.5rem;
            font-size: 1rem;
          }
          .quick-actions { grid-template-columns: 1fr; }
        }
        @media (max-width: 480px) {
          .stats-grid { grid-template-columns: 1fr 1fr; }
          .dash-root { padding: 1rem; }
          .fund-row { padding: 0.85rem 0.9rem; gap: 0.75rem; }
          .fund-row-icon { width: 40px; height: 40px; }
          .fund-row-wallet { max-width: 100px; font-size: 0.8rem; }
        }
        @media (max-width: 360px) {
          .stats-grid { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* Header */}
      <div className="dash-header">
        <div className="dash-greeting">
          <h5>Funding History</h5>
        </div>
        
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon success">
            <i className="fas fa-check-circle"></i>
          </div>
          <h4 className="stat-value">{fmtAmount(totalFunded)}</h4>
          <p className="stat-label">Total Funded</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon warning">
            <i className="fas fa-clock"></i>
          </div>
          <h4 className="stat-value">{fmtAmount(pendingTotal)}</h4>
          <p className="stat-label">Pending</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon success">
            <i className="fas fa-arrow-down"></i>
          </div>
          <h4 className="stat-value">{fmtAmount(approvedTotal)}</h4>
          <p className="stat-label">Approved</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon danger">
            <i className="fas fa-times-circle"></i>
          </div>
          <h4 className="stat-value">{fmtAmount(declinedTotal)}</h4>
          <p className="stat-label">Declined</p>
        </div>
      </div>

      {/* Quick actions */}
      <div className="quick-actions">
        <Link to="/invest" className="quick-action fund">
          <div className="qa-icon">
            <i className="fas fa-dollar-sign"></i>
          </div>
          <h6>Fund Now</h6>
          <p>Add money to your account</p>
        </Link>
        <Link to="/withdrawal" className="quick-action withdraw">
          <div className="qa-icon">
            <i className="fas fa-money-bill-wave"></i>
          </div>
          <h6>Withdraw</h6>
          <p>Withdraw trade earnings</p>
        </Link>
      </div>

      {/* History */}
      <div className="section-card">
        <h5 className="section-title">
          <i className="fas fa-history"></i> Funding Records
        </h5>

        <div className="tabs-row" role="tablist">
          <button
            type="button"
            className={`tab-btn ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            Pending <span className="tab-count">{tabCount('pending')}</span>
          </button>
          <button
            type="button"
            className={`tab-btn ${activeTab === 'approved' ? 'active' : ''}`}
            onClick={() => setActiveTab('approved')}
          >
            Approved <span className="tab-count">{tabCount('approved')}</span>
          </button>
          <button
            type="button"
            className={`tab-btn ${activeTab === 'declined' ? 'active' : ''}`}
            onClick={() => setActiveTab('declined')}
          >
            Declined <span className="tab-count">{tabCount('declined')}</span>
          </button>
        </div>

        {filteredFundings.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <i className="fas fa-inbox"></i>
            </div>
            <h6>No {activeTab} fundings</h6>
            <p>
              {activeTab === 'pending'
                ? 'You have no pending fundings at the moment.'
                : activeTab === 'approved'
                ? 'No approved funding records yet.'
                : 'No declined funding records.'}
            </p>
            {activeTab === 'pending' && (
              <Link to="/invest" className="dash-btn">
                Fund Account
              </Link>
            )}
          </div>
        ) : (
          <div className="fund-rows">
            {filteredFundings.map((f) => {
              const statusKey =
                f.status === 'waiting approval' ? 'pending' : f.status;
              const iconClass =
                statusKey === 'approved'
                  ? 'fa-check'
                  : statusKey === 'declined'
                  ? 'fa-times'
                  : 'fa-clock';
              return (
                <div key={f.id} className="fund-row">
                  <div className={`fund-row-icon ${statusKey}`}>
                    <i className={`fas ${iconClass}`}></i>
                  </div>
                  <div className="fund-row-main">
                    <span className="fund-row-amount">
                      {fmtAmount(f.amount)}
                    </span>
                    <span className="fund-row-date">
                      {fmtDate(f.created_at)}
                    </span>
                  </div>
                  <div className="fund-row-meta">
                    <span className="fund-row-wallet">
                      {f.wallet?.wallet_name || '—'}
                    </span>
                    <span className={`fund-row-status ${statusKey}`}>
                      {f.status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Funding;